import {
  splitOrderItemsBySupplierInventory,
  OrderItem,
  SupplierParts,
} from "./orderUtils";
import services from "./services";
import { getCurrentUTCTimestamp } from "../../utils/dateResolvers";
import { META_INFO_KEYS } from "../../utils/constants";
import {
  CPTSlab,
  cptSlabToDto,
} from "../../components/SupplierInventorySearch/utils/cptSlabUtils";

export interface AssignSupplierResult {
  success: boolean;
  message: string;
  isFullFulfillment?: boolean;
  allocationResponse?: any;
  newOrders?: {
    virtualOrderNo?: string;
    remainingOrderNo?: string;
    remainingOrder?: any;
    [key: string]: string | undefined;
  };
  error?: any;
  remainingOrderItems?: OrderItem[];
}

export interface FulfillmentAnalysis {
  isFullFulfillment: boolean;
  fulfilledOrderItems: OrderItem[];
  remainingOrderItems: OrderItem[];
  supplierParts: SupplierParts[];
}

export interface VirtualOrderBlockStatus {
  isBlocked: boolean;
  reason?: string;
  blockType?: "VIRTUAL" | "CUSTOMER" | "SYSTEM";
}

const BLOCK_REASONS = {
  CUSTOMER_HOLD:
    "Order is currently on customer hold. Please resume the order before assigning suppliers.",
  DELIVERED: "Cannot assign supplier to delivered order.",
  CANCELLED: "Cannot assign supplier to cancelled order.",
  FROZEN: "Order is frozen and cannot be modified.",
} as const;

export class OrderValidationService {
  static checkOrderBlockStatus(orderData: any): VirtualOrderBlockStatus {
    if (orderData.orderStatus === "CUSTOMER_HOLD") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.CUSTOMER_HOLD,
        blockType: "CUSTOMER",
      };
    }

    if (orderData.orderStatus === "DELIVERED") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.DELIVERED,
        blockType: "SYSTEM",
      };
    }

    if (orderData.orderStatus === "CANCELLED") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.CANCELLED,
        blockType: "SYSTEM",
      };
    }

    if (orderData.isCustomerOrderFreezed) {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.FROZEN,
        blockType: "SYSTEM",
      };
    }

    return { isBlocked: false };
  }

  static validateSupplierAssignment(
    orderData: any,
    supplierParts: SupplierParts[]
  ): { isValid: boolean; message?: string } {
    if (!orderData) {
      return { isValid: false, message: "Order data is required" };
    }

    if (!supplierParts || supplierParts.length === 0) {
      return { isValid: false, message: "Supplier parts are required" };
    }

    const blockStatus = this.checkOrderBlockStatus(orderData);
    if (blockStatus.isBlocked) {
      return { isValid: false, message: blockStatus.reason };
    }

    return { isValid: true };
  }
}

export class FulfillmentAnalyzer {
  static analyzeFulfillment(
    orderItems: OrderItem[],
    supplierParts: SupplierParts[]
  ): FulfillmentAnalysis {
    const { fulfilledOrderItems, remainingOrderItems } =
      splitOrderItemsBySupplierInventory(orderItems, supplierParts);

    const isFullFulfillment = remainingOrderItems.length === 0;

    return {
      isFullFulfillment,
      fulfilledOrderItems,
      remainingOrderItems,
      supplierParts,
    };
  }
}

export class SupplierAssignmentHandler {
  public static async updateOrderMetaInfo(
    orderId: string,
    setOrderMetaInfoWithOrderId: (orderId: string, metaInfo: any) => void
  ) {
    const currentUTCTimestamp = getCurrentUTCTimestamp();

    await services.updateOrderMetaInfo(orderId, {
      [META_INFO_KEYS.SUPPLIER_ALLOCATION_TIME]: currentUTCTimestamp,
    });

    setOrderMetaInfoWithOrderId(orderId, {
      [META_INFO_KEYS.SUPPLIER_ALLOCATION_TIME]: currentUTCTimestamp,
    });
  }

  static async handleFullFulfillment(
    orderId: string,
    supplierId: string,
    setOrderMetaInfoWithOrderId: (orderId: string, metaInfo: any) => void,
    supplierName: string,
    cptSlab?: CPTSlab,
    orderETA?: any
  ): Promise<AssignSupplierResult> {
    try {
      const cptSlabDto =
        cptSlab !== undefined ? cptSlabToDto(cptSlab) : undefined;

      const payload: any = {
        supplierId,
        ...(cptSlabDto && { cptSlab: cptSlabDto }),
      };

      if (orderETA) payload.orderETA = orderETA;

      const response = await services.allocateSupplierOrder(orderId, payload);

      if (!response.metaInfo?.[META_INFO_KEYS.SUPPLIER_ALLOCATION_TIME]) {
        await this.updateOrderMetaInfo(orderId, setOrderMetaInfoWithOrderId);
      }

      return {
        success: true,
        isFullFulfillment: true,
        message: `Supplier ${supplierName} assigned successfully to order no: ${response.orderNo}`,
        allocationResponse: response,
      };
    } catch (error: any) {
      return {
        success: false,
        message: JSON.stringify(error.response?.data?.errorMsg),
        error,
      };
    }
  }

  static async handlePartialFulfillment(
    orderId: string,
    supplierId: string,
    orderData: any,
    fulfilledOrderItems: OrderItem[],
    remainingOrderItems: OrderItem[],
    setOrderMetaInfoWithOrderId: (orderId: string, metaInfo: any) => void,
    supplierName: string,
    transactionType?: string,
    creditNotePercentage?: number,
    discount?: number,
    cptSlab?: CPTSlab,
    orderETA?: any,
    parentOrderETA?: any
  ): Promise<AssignSupplierResult> {
    try {
      const cptSlabDto =
        cptSlab !== undefined ? cptSlabToDto(cptSlab) : undefined;

      const response = await services.assignSupplierWithPartialFulfillment(
        orderId,
        supplierId,
        fulfilledOrderItems,
        remainingOrderItems,
        orderData,
        transactionType,
        creditNotePercentage,
        discount,
        cptSlabDto,
        orderETA,
        parentOrderETA
      );

      if (
        !response.allocationResponse.metaInfo?.[
          META_INFO_KEYS.SUPPLIER_ALLOCATION_TIME
        ]
      ) {
        await this.updateOrderMetaInfo(orderId, setOrderMetaInfoWithOrderId);
      }

      return {
        success: true,
        message: `Supplier ${supplierName} assigned successfully to order no: ${response.allocationResponse.orderNo}`,
        isFullFulfillment: false,
        newOrders: {
          virtualOrderNo: response.newOrders?.virtualOrderNo,
          remainingOrderNo: response.newOrders?.remainingOrderNo,
          remainingOrder: response.newOrders?.remainingOrder,
        },
        allocationResponse: response.allocationResponse,
      };
    } catch (error: any) {
      return {
        success: false,
        message: JSON.stringify(error.response?.data?.errorMsg),
        error,
      };
    }
  }
}

export class AssignSupplierService {
  static async assignSupplierWithFulfillmentLogic(
    orderId: string,
    supplierId: string,
    orderData: any,
    orderItems: OrderItem[],
    supplierParts: SupplierParts[],
    setOrderMetaInfoWithOrderId: (orderId: string, metaInfo: any) => void,
    supplierName: string,
    transactionType?: string,
    creditNotePercentage?: number,
    discount?: number,
    cptSlab?: CPTSlab,
    orderETA?: any,
    parentOrderETA?: any
  ): Promise<AssignSupplierResult> {
    try {
      const validation = OrderValidationService.validateSupplierAssignment(
        orderData,
        supplierParts
      );
      if (!validation.isValid) {
        return {
          success: false,
          message: validation.message || "Invalid supplier assignment",
        };
      }

      const fulfillmentAnalysis = FulfillmentAnalyzer.analyzeFulfillment(
        orderItems,
        supplierParts
      );

      if (fulfillmentAnalysis.isFullFulfillment) {
        const fullFulfillmentOrder =
          await SupplierAssignmentHandler.handleFullFulfillment(
            orderId,
            supplierId,
            setOrderMetaInfoWithOrderId,
            supplierName,
            cptSlab,
            orderETA
          );
        return {
          ...fullFulfillmentOrder,
          remainingOrderItems: fulfillmentAnalysis.remainingOrderItems,
        };
      } else {
        let partialFulfillmentOrder =
          await SupplierAssignmentHandler.handlePartialFulfillment(
            orderId,
            supplierId,
            orderData,
            fulfillmentAnalysis.fulfilledOrderItems,
            fulfillmentAnalysis.remainingOrderItems,
            setOrderMetaInfoWithOrderId,
            supplierName,
            transactionType,
            creditNotePercentage,
            discount,
            cptSlab,
            orderETA,
            parentOrderETA
          );
        return {
          ...partialFulfillmentOrder,
          remainingOrderItems: fulfillmentAnalysis.remainingOrderItems,
        };
      }
    } catch (error) {
      return {
        success: false,
        message: "Failed to assign supplier",
        error,
      };
    }
  }
}
